/* APSION ROTATION v6 (2026-07-11)
 * Daily rotation + full-catalog Re-scan for /collections/all.
 *
 * The catalog (TOTAL items in best-selling order) is a ring. Each view maps
 * grid slot g (0-based across all pages) to catalog index:
 *     k = (offset + g * stride) mod TOTAL
 * offset AND stride derive from the seed; stride is coprime with TOTAL, so
 * the map is a bijection: no duplicates, full coverage, only the true last
 * page is short.
 *
 * v6 change (2026-07-11): unified stride mixing on mobile AND desktop.
 *  - v5 mobile "light path" used stride=1 (contiguous slice) to save fetches
 *    but preserved publish-batch clumps in the underlying best-selling sort.
 *  - v6 uses the same stride candidates on both viewports. Mobile now does
 *    ~3-5 background section fetches (was 1-2) but the grid is properly
 *    de-clumped. The bandwidth cost is small (~30-100KB, cached) and the
 *    correctness win is large.
 *  - LIGHT flag is kept purely for eager-image count on the visible cards
 *    (still 4 on mobile, 8 on desktop) since that affects paint speed.
 *
 * Hardening (the catalog changes while people browse):
 *  - Section fetches carry an &apv= key derived from the marker's item count,
 *    so one view never mixes fresh and stale CDN copies.
 *  - Gap-fill: if cache skew still leaves holes, we keep walking the same
 *    bijection for substitutes so the grid never shows blank slots.
 *
 * Re-scan (apsion-discovery-bar) dispatches 'apsion:rescan' -> random seed
 * stored in sessionStorage overrides the daily seed for this session.
 */
(function () {
  var meta = document.querySelector('[data-apsion-rotate]');
  if (!meta) return;
  if (location.pathname.indexOf('/collections/all') !== 0) return;

  // Leave filtered / sorted / search views alone.
  var params = new URLSearchParams(location.search);
  var skip = false;
  params.forEach(function (v, k) {
    if (k.indexOf('filter.') === 0 || k === 'sort_by' || k === 'q') skip = true;
  });
  if (skip) return;

  var PAGES = parseInt(meta.getAttribute('data-pages'), 10) || 1;
  var CURRENT = parseInt(meta.getAttribute('data-current'), 10) || 1;
  var SECTION = meta.getAttribute('data-section');
  var TOTAL = parseInt(meta.getAttribute('data-items'), 10) || 0;
  var PER = parseInt(meta.getAttribute('data-per-page'), 10) || 32;
  if (PAGES < 2 || !SECTION || !TOTAL || !PER) return;

  // LIGHT still governs how many hero images we eager-load (paint speed).
  // As of v6 it no longer changes the stride selection.
  var LIGHT = false;
  try {
    LIGHT = window.matchMedia('(max-width: 749px)').matches
      || (navigator.connection && navigator.connection.saveData) === true;
  } catch (e) {}

  /* ---- seeded PRNG (mulberry32) + Fisher-Yates ---- */
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function shuffled(arr, seed) {
    var rand = mulberry32(seed);
    var a = arr.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(rand() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  /* ---- seed: re-scan override wins, else day number in store time ---- */
  function daySeed() {
    try {
      var s = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
      return parseInt(s.replace(/-/g, ''), 10);
    } catch (e) {
      return Math.floor(Date.now() / 86400000);
    }
  }
  function currentSeed() {
    try {
      var o = sessionStorage.getItem('apsion_rotation_seed');
      if (o) return parseInt(o, 10);
    } catch (e) {}
    return daySeed();
  }

  function gcd(a, b) { while (b) { var t = a % b; a = b; b = t; } return a; }

  /* offset + stride from the seed. Stride candidates are small so fetches
     stay manageable, and must be coprime with TOTAL so the slot->item map
     is a bijection. Same on mobile and desktop as of v6. */
  function planFor(seed) {
    var rand = mulberry32(seed);
    var offset = Math.floor(rand() * TOTAL);
    var stride = 1;
    var candidates = shuffled([3, 4, 5, 6, 7, 8, 9, 10, 11, 13], seed ^ 0x9E3779B9);
    for (var i = 0; i < candidates.length; i++) {
      if (gcd(candidates[i], TOTAL) === 1) { stride = candidates[i]; break; }
    }
    return { offset: offset, stride: stride };
  }

  function findGrid() {
    return document.querySelector('results-list .product-grid, results-list ul[class*="product-grid"]');
  }

  var grid = findGrid();
  if (!grid) return;

  // Claim the grid before apsion-shuffle can reorder it, and prep the fade.
  grid.setAttribute('data-apsion-shuffled', 'true');
  grid.style.transition = 'opacity 0.18s ease';

  var revealTimer = null;
  function reveal() {
    if (revealTimer) { clearTimeout(revealTimer); revealTimer = null; }
    grid.style.opacity = '';
  }

  /* Source-page node cache. Page CURRENT is seeded from the server DOM,
     which by definition matches the marker's catalog state. */
  var pageCache = {};
  pageCache[CURRENT] = Array.prototype.slice.call(grid.children).filter(function (n) {
    return n.getAttribute && n.getAttribute('data-product-id');
  });

  function fetchSourcePage(p) {
    if (pageCache[p]) return Promise.resolve(pageCache[p]);
    // apv keys the CDN cache to this view's catalog state so one view never
    // mixes fresh and stale renders while the catalog is being edited.
    var url = location.pathname
      + '?section_id=' + encodeURIComponent(SECTION)
      + '&page=' + p
      + '&apv=' + TOTAL + '-' + PAGES;
    return fetch(url)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = doc.querySelectorAll('li.product-grid__item[data-product-id]');
        var nodes = Array.prototype.slice.call(items).map(function (n) {
          return document.importNode(n, true);
        });
        pageCache[p] = nodes;
        return nodes;
      });
  }

  /* Promote the first visible cards' images so shirts paint immediately
     after the swap instead of waiting for lazy loading. */
  function prioritizeImages(items) {
    var eager = LIGHT ? 4 : 8;
    for (var i = 0; i < items.length && i < eager; i++) {
      var img = items[i].querySelector('img');
      if (!img) continue;
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', i < 4 ? 'high' : 'auto');
      img.setAttribute('decoding', 'async');
    }
  }

  var runId = 0;
  async function applySeed(seed) {
    var myRun = ++runId;
    var plan = planFor(seed);

    // Slots for this grid page mirror the server's page size so totals
    // always line up (only the real last page is short).
    var base = (CURRENT - 1) * PER;
    var slots = Math.min(PER, TOTAL - base);
    var idxs = [];
    for (var i = 0; i < slots; i++) {
      idxs.push((plan.offset + (base + i) * plan.stride) % TOTAL);
    }

    var needed = [];
    idxs.forEach(function (k) {
      var p = Math.floor(k / PER) + 1;
      if (needed.indexOf(p) === -1) needed.push(p);
    });

    grid.style.opacity = '0';
    // Never leave the grid hidden: reveal after 2s no matter what.
    if (revealTimer) clearTimeout(revealTimer);
    revealTimer = setTimeout(reveal, 2000);

    try {
      await Promise.all(needed.map(fetchSourcePage));
      if (myRun !== runId) return;

      var nodes = [];
      var used = {};
      function take(k) {
        var p = Math.floor(k / PER) + 1;
        var arr = pageCache[p];
        var n = arr && arr[k % PER];
        if (!n) return false;
        var id = n.getAttribute('data-product-id');
        if (used[id]) return false;
        used[id] = 1;
        nodes.push(n.cloneNode(true));
        return true;
      }
      idxs.forEach(take);

      // Gap-fill: if cache skew left holes, keep walking the same bijection
      // (next global slots) so the page never shows blank slots.
      var g = base + slots;
      var guard = TOTAL;
      while (nodes.length < slots && guard-- > 0) {
        var k = (plan.offset + (g % TOTAL) * plan.stride) % TOTAL;
        var p = Math.floor(k / PER) + 1;
        if (!pageCache[p]) {
          try { await fetchSourcePage(p); } catch (e) { break; }
          if (myRun !== runId) return;
        }
        take(k);
        g++;
      }

      if (!nodes.length) { reveal(); return; }
      var items = shuffled(nodes, (seed * 31 + CURRENT) | 0);
      prioritizeImages(items);
      var frag = document.createDocumentFragment();
      items.forEach(function (n) { frag.appendChild(n); });
      while (grid.firstChild) grid.removeChild(grid.firstChild);
      grid.appendChild(frag);
      requestAnimationFrame(reveal);
      document.dispatchEvent(new CustomEvent('apsion:rotation:done'));
    } catch (e) {
      // Fetch failed: keep whatever is currently shown.
      reveal();
      document.dispatchEvent(new CustomEvent('apsion:rotation:done'));
    }
  }

  function boot() { applySeed(currentSeed()); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('apsion:rescan', function () {
    var seed = ((Date.now() % 2147483647) ^ Math.floor(Math.random() * 1e9)) | 0;
    if (seed < 0) seed = -seed;
    try { sessionStorage.setItem('apsion_rotation_seed', String(seed)); } catch (e) {}
    applySeed(seed);
  });

  // Tell the discovery bar the new engine is active on this page.
  window.__apsionRotation = true;
  document.documentElement.setAttribute('data-apsion-rotation', '1');
})();
